import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, PieChart, Pie, Cell, Legend, AreaChart, Area,
} from "recharts";
import {
  Star, TrendingUp, ThumbsUp, AlertTriangle, ChevronLeft, Users, Sparkles,
  BadgeCheck, Frown, Heart, ShieldAlert, MessageSquare, FileDown,
} from "lucide-react";
import { BACKEND_URL } from "@/lib/api";

const COLORS = {
  positive: "#16a34a", neutral: "#FFD600", negative: "#FF3B30",
  brand: "#FF3B30", ink: "#0A0A0A", cyan: "#00E5FF", soft: "#FFF6E5",
};

function monthLabel(ym) {
  if (!ym || ym.length < 7) return ym || "";
  const [y, m] = ym.split("-");
  const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${names[parseInt(m, 10) - 1] || m} ${y.slice(2)}`;
}

export default function AdminSatisfaction() {
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/satisfaction").then(({ data }) => setData(data));
    api.get("/admin/satisfaction/insights")
      .then(({ data }) => setInsights(data))
      .finally(() => setInsightsLoading(false));
  }, []);

  const monthly = useMemo(
    () => (data?.monthly || []).map((m) => ({ ...m, label: monthLabel(m.month) })),
    [data],
  );

  const sentimentPie = useMemo(() => {
    if (!data) return [];
    const s = data.by_sentiment || {};
    return [
      { name: "Positive", value: s.positive || 0, color: COLORS.positive },
      { name: "Neutral", value: s.neutral || 0, color: COLORS.neutral },
      { name: "Negative", value: s.negative || 0, color: COLORS.negative },
    ];
  }, [data]);

  if (!data) return <div className="py-16 text-center text-sm text-muted-foreground">Loading satisfaction dashboard…</div>;

  const csatBand = data.csat_pct >= 80 ? "text-green-600" : data.csat_pct >= 60 ? "text-brand-yellow" : "text-brand-orange";
  const sinceDate = data.since ? new Date(data.since).toLocaleDateString("en-IN", { month: "short", year: "numeric" }) : "—";

  return (
    <div className="space-y-6" data-testid="admin-satisfaction-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Link to="/admin" className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground">
            <ChevronLeft size={12} /> Back to admin
          </Link>
          <h1 className="mt-2 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">
            Customer satisfaction
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Live CSAT tracking across every customer served since <b>{sinceDate}</b>. Powered by real post-service reviews.
          </p>
        </div>
        <a
          href={`${BACKEND_URL}/api/admin/satisfaction/export.pdf`}
          target="_blank"
          rel="noreferrer"
          data-testid="satisfaction-pdf-export"
          className="inline-flex items-center gap-2 rounded-full bg-brand-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-ink/90"
        >
          <FileDown size={14} /> Export board PDF
        </a>
      </div>

      {/* Hero KPIs */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <HeroTile
          label="Overall satisfaction"
          value={`${data.csat_pct}%`}
          hint={`${data.promoters} of ${data.total} customers rated 4★ or 5★`}
          icon={Heart}
          accent={csatBand}
          bg="bg-gradient-to-br from-brand-orange/10 to-brand-yellow/10"
        />
        <HeroTile
          label="Avg rating (all time)"
          value={`${(data.avg_rating || 0).toFixed(2)} / 5`}
          hint={`${data.total} verified reviews`}
          icon={Star}
          accent="text-brand-yellow"
          bg="bg-white"
        />
        <HeroTile
          label="Promoters"
          value={data.promoters}
          hint="4★ & 5★ reviews"
          icon={ThumbsUp}
          accent="text-green-600"
          bg="bg-white"
        />
        <HeroTile
          label="Detractors"
          value={data.detractors}
          hint="1★ & 2★ reviews — needs care"
          icon={ShieldAlert}
          accent="text-brand-orange"
          bg="bg-white"
        />
      </div>

      {/* AI insights */}
      <Card title="Key insights (AI)" icon={Sparkles} accent={COLORS.brand}>
        {insightsLoading ? (
          <div className="grid h-[120px] place-items-center text-sm text-muted-foreground">Generating insights…</div>
        ) : insights?.sample_size === 0 ? (
          <Empty msg="No customer reviews collected yet. Once customers rate completed bookings, AI insights appear here." />
        ) : (
          <div className="space-y-4">
            <div className="rounded-2xl bg-brand-yellow/10 p-4 text-sm leading-relaxed">
              <div className="mb-1 flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                Summary · {insights.sample_size} reviews analysed
                {insights.generated ? (
                  <span className="rounded-full bg-brand-orange/10 px-2 py-0.5 text-[9px] text-brand-orange">AI</span>
                ) : (
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[9px]">Fallback</span>
                )}
              </div>
              {insights.summary}
            </div>
            <div className="grid gap-3 lg:grid-cols-2">
              <ThemeList
                title="What customers love"
                icon={BadgeCheck}
                items={insights.praise}
                accent={COLORS.positive}
                testid="praise-themes"
              />
              <ThemeList
                title="What needs attention"
                icon={Frown}
                items={insights.concerns}
                accent={COLORS.brand}
                testid="concern-themes"
              />
            </div>
          </div>
        )}
      </Card>

      {/* Monthly trend + rating dist */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Monthly satisfaction trend" icon={TrendingUp} accent={COLORS.ink}>
          {monthly.length === 0 ? (
            <Empty msg="No monthly data yet. Once reviews accumulate they'll graph here." />
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={monthly} margin={{ top: 10, right: 20, bottom: 0, left: -10 }}>
                <defs>
                  <linearGradient id="csatArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COLORS.brand} stopOpacity={0.4} />
                    <stop offset="100%" stopColor={COLORS.brand} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f1" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis yAxisId="left" domain={[0, 100]} tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} formatter={(v, k) => k === "csat_pct" ? [`${v}%`, "CSAT"] : k === "count" ? [v, "Reviews"] : [v, k]} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area yAxisId="left" type="monotone" dataKey="csat_pct" stroke={COLORS.brand} strokeWidth={2.5} fill="url(#csatArea)" name="CSAT %" />
                <Line yAxisId="right" type="monotone" dataKey="count" stroke={COLORS.ink} strokeWidth={1.5} dot={{ r: 3 }} name="Reviews" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card title="Rating distribution" icon={Star} accent={COLORS.brand}>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.rating_distribution} margin={{ top: 10, right: 20, bottom: 0, left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f1" />
              <XAxis dataKey="rating" tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}★`} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                {data.rating_distribution.map((r, i) => (
                  <Cell key={i} fill={r.rating >= 4 ? COLORS.positive : r.rating === 3 ? COLORS.neutral : COLORS.brand} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Sentiment mix + Top / Bottom providers */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="Sentiment mix" icon={MessageSquare} accent={COLORS.ink}>
          {sentimentPie.every((s) => s.value === 0) ? (
            <Empty msg="Waiting for reviews with comments." />
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={sentimentPie} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={3}>
                  {sentimentPie.map((s, i) => <Cell key={i} fill={s.color} />)}
                </Pie>
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card title="Top rated providers" icon={ThumbsUp} accent={COLORS.positive}>
          <RankList items={data.top_providers} labelKey="name" testid="top-providers" accent={COLORS.positive} />
        </Card>

        <Card title="Providers needing coaching" icon={AlertTriangle} accent={COLORS.brand}>
          <RankList items={data.needs_attention_providers} labelKey="name" testid="needs-attention" accent={COLORS.brand} />
        </Card>
      </div>

      {/* Top services + Recent negatives */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Top rated services" icon={Users} accent={COLORS.cyan}>
          <RankList items={data.top_services} labelKey="title" testid="top-services" accent={COLORS.cyan} />
        </Card>
        <Card title="Recent 1★ / 2★ reviews" icon={ShieldAlert} accent={COLORS.brand}>
          {data.recent_negatives.length === 0 ? (
            <Empty msg="No negative reviews. Keep it up!" />
          ) : (
            <ul className="space-y-2" data-testid="recent-negatives">
              {data.recent_negatives.map((r) => (
                <li key={r.id} className="rounded-xl border p-3 text-sm">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-semibold">{r.customer_name} · {r.service_title}</span>
                    <span className="font-mono font-bold text-brand-orange">{r.rating}★</span>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">"{r.comment || 'No comment provided.'}"</div>
                  <div className="mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">
                    {(r.created_at || "").slice(0, 10)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

function HeroTile({ label, value, hint, icon: Icon, accent, bg }) {
  return (
    <div className={`rounded-2xl border border-foreground/10 p-5 ${bg}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{label}</div>
        <Icon size={16} className={accent} />
      </div>
      <div className={`mt-2 font-heading text-3xl font-extrabold tracking-tight ${accent}`}>{value}</div>
      <div className="mt-1 text-[11px] text-muted-foreground">{hint}</div>
    </div>
  );
}

function Card({ title, icon: Icon, accent, children }) {
  return (
    <div className="rounded-2xl border border-foreground/10 bg-white p-5">
      <div className="mb-4 flex items-center gap-2">
        <Icon size={14} style={{ color: accent }} />
        <div className="font-heading text-sm font-bold uppercase tracking-widest">{title}</div>
      </div>
      {children}
    </div>
  );
}

function Empty({ msg }) {
  return <div className="grid h-[140px] place-items-center text-xs text-muted-foreground">{msg}</div>;
}

function ThemeList({ title, icon: Icon, items, accent, testid }) {
  return (
    <div className="rounded-2xl border p-4" data-testid={testid}>
      <div className="mb-2 flex items-center gap-2">
        <Icon size={14} style={{ color: accent }} />
        <div className="font-heading text-xs font-bold uppercase tracking-widest">{title}</div>
      </div>
      {!items || items.length === 0 ? (
        <div className="text-xs text-muted-foreground">Not enough data yet.</div>
      ) : (
        <ul className="space-y-1.5">
          {items.map((t, i) => (
            <li key={i} className="flex gap-2 text-sm leading-snug">
              <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: accent }} />
              <span>{t}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RankList({ items, labelKey, testid, accent }) {
  if (!items || items.length === 0) return <Empty msg="No entries yet." />;
  return (
    <ol className="space-y-2" data-testid={testid}>
      {items.map((r, i) => (
        <li key={r.id || i} className="flex items-center justify-between rounded-xl border p-3">
          <div className="flex items-center gap-3">
            <span
              className="grid h-7 w-7 place-items-center rounded-full text-xs font-bold text-white"
              style={{ background: accent }}
            >
              {i + 1}
            </span>
            <div>
              <div className="font-heading text-sm font-bold">{r[labelKey] || "-"}</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{r.count} reviews</div>
            </div>
          </div>
          <div className="font-mono font-bold">
            {r.avg_rating.toFixed(1)}<span className="ml-1 text-brand-yellow">★</span>
          </div>
        </li>
      ))}
    </ol>
  );
}
